import {css, useTheme} from '@mui/material';
import {DefaultScroll, GetStylesFunction, useDefaultScroll} from '@wallaby-ui/core';
import React, {CSSProperties, useCallback, useEffect, useMemo, useRef} from 'react';

function parsePx(px: string | number): number {
  const pxString = String(px);
  const valueString = pxString.endsWith('px') ? pxString.slice(0, -2) : pxString;
  const value = Number.isNaN(+valueString) ? 0 : +valueString;
  return value;
}

type Props = {
  xOffset?: number;
  defaultScroll?: DefaultScroll;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  className?: string;
  style?: CSSProperties;
};
const LightScrollbarWrapperA: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {xOffset = 0, className, defaultScroll, onScroll: _onScroll, style, children, ...extraProps} = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme, params: {xOffset}});
  // on mount
  const scrollContainerRef = useRef(null as HTMLDivElement | null);
  const scrollWrapperRef = useRef(null as HTMLDivElement | null);
  const scrollbarRef = useRef(null as HTMLDivElement | null);
  const scrollBoxHeight = useRef(0);
  const scrollHeight = useRef(0);
  const scrollbarSize = useRef(0);
  const resizeScrollbar = useCallback(() => {
    if (!scrollWrapperRef.current || !scrollbarRef.current) return;
    const scrollContainerStyle = scrollContainerRef.current ? getComputedStyle(scrollContainerRef.current) : null;
    const paddingOffset = parsePx(scrollContainerStyle?.paddingTop ?? 0) + parsePx(scrollContainerStyle?.paddingBottom ?? 0);
    scrollBoxHeight.current = scrollWrapperRef.current.getBoundingClientRect().height + paddingOffset;
    scrollHeight.current = scrollWrapperRef.current.scrollHeight + paddingOffset;
    scrollbarSize.current = (scrollBoxHeight.current / scrollHeight.current) * scrollBoxHeight.current;
    scrollbarRef.current.style.height = `${Math.floor(scrollbarSize.current)}px`;
    scrollbarRef.current.style.display = scrollHeight.current > scrollBoxHeight.current ? 'block' : 'none';
  }, []);
  const moveScrollbar = useCallback(() => {
    if (!scrollWrapperRef.current || !scrollbarRef.current) return;
    resizeScrollbar();
    const scrollY = scrollWrapperRef.current.scrollTop;
    const scrollMax = scrollHeight.current - scrollBoxHeight.current;
    const scrollOffset = (scrollY / (scrollMax || 1)) * (scrollBoxHeight.current - scrollbarSize.current);
    scrollbarRef.current.style.top = `${scrollOffset}px`;
    // eslint-disable-next-line
  }, []);
  // on mount
  const setScrollWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollWrapperRef.current = node;
      if (scrollWrapperRef.current) {
        moveScrollbar();
      }
    },
    [moveScrollbar]
  );
  // on default scroll change
  useDefaultScroll(scrollWrapperRef, defaultScroll);
  // on rerender
  useEffect(() => {
    moveScrollbar();
  });
  // on resize
  const resizeObserver = useMemo(() => {
    return new ResizeObserver(moveScrollbar);
    // eslint-disable-next-line
  }, [])
  const scrollContentRef = useRef(null as HTMLDivElement | null);
  const setScrollContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollContentRef.current) {
        resizeObserver.unobserve(scrollContentRef.current);
      }
      scrollContentRef.current = node;
      if (scrollContentRef.current) {
        moveScrollbar();
        resizeObserver.observe(scrollContentRef.current);
      }
    },
    [moveScrollbar, resizeObserver]
  );
  // on scroll
  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      moveScrollbar();
      if (_onScroll) _onScroll(event);
    },
    // eslint-disable-next-line
    [_onScroll]
  );
  // on drag
  const dragStartScrollY = useRef(-999);
  const dragStartPos = useRef(-999);
  const onDrag = useCallback((pos: number) => {
    if (pos === 0) return;
    if (dragStartScrollY.current < 0) return; // NOTE: we are not actually dragging
    if (dragStartPos.current < 0) {
      dragStartPos.current = pos; // NOTE: start dragging from here
    }
    const newScrollY = dragStartScrollY.current + (pos - dragStartPos.current) * (scrollHeight.current / scrollBoxHeight.current);
    setTimeout(() => {
      if (scrollWrapperRef.current) scrollWrapperRef.current.scrollTo(0, newScrollY);
    }, 250);
  }, []);
  const onMouseMove = useCallback(
    (event) => onDrag(event.clientY),
    // eslint-disable-next-line
   []);
  const onTouchMove = useCallback(
    (event) => onDrag(event.touches[0].clientY),
    // eslint-disable-next-line
    []
  );
  const endDragging = useCallback(() => {
    dragStartScrollY.current = -999;
    dragStartPos.current = -999;
    document.body.removeEventListener('mousemove', onMouseMove);
    document.body.removeEventListener('touchmove', onTouchMove);
    document.body.removeEventListener('mouseup', endDragging);
    document.body.removeEventListener('touchend', endDragging);
    // eslint-disable-next-line
  }, []);
  const startDragging = useCallback((target: HTMLElement | null) => {
    dragStartScrollY.current = scrollWrapperRef.current?.scrollTop ?? -999;
    dragStartPos.current = -999;
    document.body.addEventListener('mousemove', onMouseMove);
    document.body.addEventListener('touchmove', onTouchMove);
    document.body.addEventListener('mouseup', endDragging);
    document.body.addEventListener('touchend', endDragging);
    // eslint-disable-next-line
  }, []);
  const preventDefaultDrag = useCallback((event) => {
    event.preventDefault();
  }, []);
  return (
    <div css={muiStyles.scrollContainerWrapper}>
      <div {...extraProps} ref={setScrollWrapperRef} className={className} onScroll={onScroll} css={muiStyles.scrollWrapper} style={style}>
        <div ref={setScrollContentRef}>{children}</div>
      </div>
      <div
        ref={scrollbarRef}
        css={muiStyles.scrollbar}
        onDragStart={preventDefaultDrag}
        onMouseDown={(event) => startDragging(event.target as HTMLElement | null)}
        onTouchStart={(event) => startDragging(event.touches[0].target as HTMLElement | null)}
      />
    </div>
  );
};
const LightScrollbarWrapperB: React.FC<React.PropsWithChildren<Props>> = ({xOffset: _, defaultScroll, style, children, ...extraProps}) => {
  const scrollRef = useRef(null as HTMLDivElement | null);
  useDefaultScroll(scrollRef, defaultScroll);
  return (
    <div
      {...extraProps}
      ref={scrollRef}
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        ...style,
      }}
    >
      {children}
    </div>
  );
};
// export const LightScrollbarWrapper = LightScrollbarWrapperA;
export const LightScrollbarWrapper = LightScrollbarWrapperB;

const getStyles: GetStylesFunction = (options) => ({
  scrollContainerWrapper: css`
    width: 100%;
    height: 100%;
    display: block;
    overflow: hidden;
    position: relative;
  `,
  scrollWrapper: css`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      width: 0;
    }
  `,
  scrollbar: css`
    position: absolute;
    right: ${options?.params?.xOffset ?? 0}px;
    top: 0;
    width: var(--dlScrollbarSize);
    border-radius: var(--dlScrollbarBorderRadius);
    height: 10px;
    background: var(--dlScrollbarThumbInactiveColor);
    user-select: none;
    z-index: 99;
    &:hover {
      background: var(--dlScrollbarThumbColor);
    }
  `,
});
